import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isFrameElement, SketchElement } from '../../../inspector'
import { filterTree, findAncestorIdentifiers, flattenTree } from './treeUtils'
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual'
import { FlatTreeNode as FlatTreeLayer } from './types'

/**
 * State and utils to keep track of the collapsed/expanded state of each layer in the tree.
 */
export function useCollapsedState() {
  const [collapsedElements, setCollapsedElements] = useState<Set<bigint>>(
    new Set()
  )

  const toggleCollapse = useCallback((identifier: bigint) => {
    setCollapsedElements(prev => {
      const newSet = new Set(prev)
      if (newSet.has(identifier)) {
        newSet.delete(identifier)
      } else {
        newSet.add(identifier)
      }
      return newSet
    })
  }, [])

  const expandAllAncestors = useCallback(
    (element: SketchElement) => {
      const ancestors = findAncestorIdentifiers(element)
      if (ancestors.length > 0) {
        setCollapsedElements(prev => {
          const newSet = new Set(prev)
          // Remove from the set of collapsed elements to expand them
          ancestors.forEach(ancestorId => newSet.delete(ancestorId))
          return newSet
        })
      }
    },
    [setCollapsedElements]
  )

  return {
    collapsedElements,
    toggleCollapse,
    setCollapsedElements,
    expandAllAncestors,
  }
}

/**
 * Get the list of top-level layers. If the root element is a page, then we take its children,
 * if it's a frame, we take the frame itself.
 */
function useTreeElements(sketchSceneRootElement: SketchElement | null) {
  // For pages, show only the children (all frames/artboards)
  // For frames, show the frame itself and its children
  const topLevelElements = useMemo(() => {
    if (!sketchSceneRootElement) return []

    return isFrameElement(sketchSceneRootElement)
      ? [sketchSceneRootElement]
      : sketchSceneRootElement.children
  }, [sketchSceneRootElement])

  return topLevelElements
}

/**
 * Filter the tree based on a search query
 * Layers where the name doesn't match are removed,
 * unless they have children that match.
 */
export function useFilteredTree(
  sketchSceneRootElement: SketchElement | null,
  searchQuery: string
) {
  const topLevelElements = useTreeElements(sketchSceneRootElement)

  const topLevelElementsFiltered = useMemo(() => {
    return filterTree(topLevelElements, searchQuery)
  }, [topLevelElements, searchQuery])

  return topLevelElementsFiltered
}

/**
 * Get the tree structure flattened and virtualized for performance.
 */
export function useVirtualizedFlattenedTree(
  topLevelElements: SketchElement[],
  collapsedElements: Set<bigint>
) {
  const virtualContainerRef = useRef<HTMLDivElement>(null)

  // Tanstack virtualizer needs a flat list of elements for the
  // virtualization to work. So we transform the tree structure into a flat list
  // with a "level" property indicating the depth in the tree.
  const visibleLayersFlatList = useMemo(
    () => flattenTree(topLevelElements, collapsedElements),
    [topLevelElements, collapsedElements]
  )

  const virtualizer = useVirtualizer({
    count: visibleLayersFlatList.length,
    getScrollElement: () => virtualContainerRef.current,
    estimateSize: () => 28, // Approximate height of each row
    overscan: 5,
    scrollPaddingStart: 12,
    scrollPaddingEnd: 12,
  })

  return {
    virtualContainerRef,
    virtualizer,
    visibleLayersFlatList,
  }
}

/**
 * Whenever the layer selection changes, automatically expand all ancestor layers.
 */
export function useAutoExpandTreeOnSelection(
  selectedElement: SketchElement | null,
  topLevelElements: SketchElement[],
  expandAllAncestors: (element: SketchElement) => void
) {
  const previousSelectedIdentifierRef = useRef<bigint | undefined>(undefined)

  // Expand ancestors when an element is selected (only when selection changes)
  useEffect(() => {
    const currentIdentifier = selectedElement?.identifier
    const previousIdentifier = previousSelectedIdentifierRef.current

    // Only expand if the selection actually changed
    if (
      selectedElement &&
      topLevelElements.length > 0 &&
      currentIdentifier !== previousIdentifier
    ) {
      expandAllAncestors(selectedElement)
    }

    // Update the ref to track current selection
    previousSelectedIdentifierRef.current = currentIdentifier
  }, [selectedElement, topLevelElements, expandAllAncestors])
}

/**
 * Whenever the layer selection changes, automatically scroll to the layer to make it visible.
 */
export function useAutoScrollToSelection(
  virtualizer: Virtualizer<HTMLDivElement, Element>,
  selectedElement: SketchElement | null,
  visibleLayersFlatList: FlatTreeLayer[]
) {
  const previousSelectedIdentifierRef = useRef<bigint | undefined>(undefined)

  useEffect(() => {
    const currentIdentifier = selectedElement?.identifier
    const previousIdentifier = previousSelectedIdentifierRef.current

    // Only scroll if the selection actually changed
    // See https://linear.app/sketch/issue/SWEB-2801/erratic-behavior-when-collapsingexpanding-parent-groups
    if (selectedElement && currentIdentifier !== previousIdentifier) {
      const index = visibleLayersFlatList.findIndex(
        item => item.sketchElement.identifier === selectedElement.identifier
      )
      if (index !== -1) {
        virtualizer.scrollToIndex(index, { align: 'auto' })
      }
    }

    // Update the ref to track current selection
    previousSelectedIdentifierRef.current = currentIdentifier
  }, [selectedElement, visibleLayersFlatList, virtualizer])
}

/**
 * Hook to manage context menu state and positioning for layer tree items
 */
export function useLayerTreeContextMenu() {
  const [contextMenuState, setContextMenuState] = useState<{
    position: { x: number; y: number } | null
    targetElement: SketchElement | null
  }>({
    position: null,
    targetElement: null,
  })

  const handleContextMenu = useCallback(
    (event: React.MouseEvent, targetElement: SketchElement) => {
      event.preventDefault()
      event.stopPropagation()

      // Get the position relative to the page for fixed positioning
      const position = {
        x: event.pageX,
        y: event.pageY,
      }

      setContextMenuState({
        position,
        targetElement,
      })

      // Dispatch event to notify other context menus to close
      window.dispatchEvent(new CustomEvent('layer-tree-context-menu-opened'))
    },
    []
  )

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuState({
      position: null,
      targetElement: null,
    })
  }, [])

  // Close context menu when right-clicking elsewhere
  useEffect(() => {
    if (!contextMenuState.position) return

    const handleGlobalContextMenu = (event: MouseEvent) => {
      // Check if the right-click is on a layer tree item
      const target = event.target as Element
      const isLayerTreeItem = target?.closest('[role="treeitem"]')

      // If not clicking on a layer tree item, close the menu
      if (!isLayerTreeItem) {
        handleCloseContextMenu()
      }
    }

    document.addEventListener('contextmenu', handleGlobalContextMenu)
    return () => {
      document.removeEventListener('contextmenu', handleGlobalContextMenu)
    }
  }, [contextMenuState.position, handleCloseContextMenu])

  // Close layer tree context menu when canvas context menu opens
  useEffect(() => {
    const handleCanvasContextMenuOpened = () => {
      if (contextMenuState.position) {
        handleCloseContextMenu()
      }
    }

    window.addEventListener(
      'canvas-context-menu-opened',
      handleCanvasContextMenuOpened
    )
    return () => {
      window.removeEventListener(
        'canvas-context-menu-opened',
        handleCanvasContextMenuOpened
      )
    }
  }, [contextMenuState.position, handleCloseContextMenu])

  return {
    contextMenuPosition: contextMenuState.position,
    contextMenuTargetElement: contextMenuState.targetElement,
    handleContextMenu,
    handleCloseContextMenu,
  }
}
